import React, { useEffect, FC, useRef, useState } from 'react';
import { twMerge } from 'tailwind-merge';
import faceImg from 'static/assets/img/mock-face.png';
import { createPlaySoundFunction } from '../helpers';
import tickSound from 'static/assets/audio/tick1.ogg';
import { computed } from '@preact/signals-react';
import { demoState, userAnswer } from 'apis/general';
type GameSliderProps = {
  onValueChange?: (value: number) => void;
  isInteractive?: boolean;
  className?: string;
};

const sliderToGameValue = (sliderValue: number) => sliderValue * 4; // The multiplier function for the game
const didUserAnswer = computed(() => userAnswer.value >= 0);
const isSliderInteractive = computed(() => userAnswer.value < 0 && demoState.value < 3);

/** This is the main game slider for the alle-mod-1 gameshow model */
export const GameSlider: FC<GameSliderProps> = ({ className = '', onValueChange }) => {
  const sliderRef = useRef<HTMLDivElement | null>(null);
  const [value, setValue] = useState(50); // Default to middle point
  const playSlideSound = useRef(createPlaySoundFunction(tickSound, 0.7)).current;

  const handleScroll = () => {
    if (!sliderRef.current) return;
    const scrollLeft = sliderRef.current.scrollLeft;
    const maxScrollLeft = sliderRef.current.scrollWidth - sliderRef.current.clientWidth;
    const scrollPercent = (scrollLeft / maxScrollLeft) * 100;
    const adjustedValue = Math.round(scrollPercent);
    setValue(adjustedValue);
    onValueChange?.(sliderToGameValue(adjustedValue));
  };

  useEffect(() => {
    const slider = sliderRef.current;
    if (!slider) return;

    setTimeout(() => {
      const maxScrollLeft = slider.scrollWidth - slider.clientWidth;
      slider.scrollLeft = maxScrollLeft / 2;
    }, 10);

    slider.addEventListener('scroll', handleScroll);
    return () => {
      slider.removeEventListener('scroll', handleScroll);
    };
  }, [sliderRef.current]);

  useEffect(() => {
    playSlideSound();
  }, [value]);

  return (
    <div className={twMerge('flex flex-col items-center relative overflow-x-visible', className)}>
      {!isSliderInteractive.value && (
        <>
          <p className="absolute left-0 bottom-[35%]">0</p>
          <p className="absolute right-0 bottom-[35%]">400</p>
        </>
      )}

      {/* Fixed player indicator */}
      <div className="absolute bottom-0 flex flex-col justify-center items-center" style={{ left: isSliderInteractive.value ? 'auto' : `calc(${value}% - 24px)` }}>
        {!(!didUserAnswer.value && demoState.value >= 3) && (
          <div className={`bg-yellow-400 rounded-full z-10 ${isSliderInteractive.value ? 'top-[-4.2rem] h-[4.5rem]' : 'top-[-3.5rem] h-[3.5rem]'} w-0.5 absolute `} />
        )}
        <div className="absolute left-[60px] w-[100px] text-left text-lg font-bold">
          {didUserAnswer.value && `${userAnswer.value} poser`}
          {!didUserAnswer.value && demoState.value < 3 && `${sliderToGameValue(value)} poser`}
          {!didUserAnswer.value && demoState.value >= 3 && 'Tiden udløb'}
        </div>
        <div className="w-12 h-12 z-30 rounded-full flex items-center justify-center pointer-events-none">
          {/* Placeholder for player icon */}
          <img src={faceImg} alt="Player" className="w-full h-full border-2 border-yellow-400 rounded-full bg-yellow-400" />
        </div>
      </div>

      {!isSliderInteractive.value && getFriend1Answer()}
      {!isSliderInteractive.value && getFriend2Answer()}
      {demoState.value > 3 && getStudioAnswer()}
      {demoState.value > 4 && getCorrectAnswer()}
      {demoState.value > 5 && getDenmarkAnswer()}

      {/* Scrollable bar */}
      {isSliderInteractive.value ? (
        <div ref={sliderRef} className="relative overflow-x-auto w-full h-40 pt-8 scrollbar-hide hidden-scrollbar" style={{ width: '1000px' }}>
          <div className="flex flex-row border-b-2  border-white border-solid pb-1 gap-1.5 mx-[calc(50%-2px)] " style={{ width: '1000px' }}>
            {Array.from({ length: 101 }, (_, i) => (
              <div key={i} className={`flex flex-col items-center justify-end w-1 ${i % 5 === 0 ? 'text-white' : 'text-gray-600'}`}>
                {/* Major marks with labels every 10 units */}
                {i % 10 === 0 && <span className="text-xs">{sliderToGameValue(i)}</span>}
                <div className={`w-full rounded-full ${i % 5 === 0 ? 'h-10' : 'h-8'} bg-white ${Math.round(value) === i ? 'scale-[1.15]' : ''}`}></div>
              </div>
            ))}
          </div>
        </div>
      ) : (
        <div className="w-full h-52 flex flex-col items-center justify-center">
          <div className="border-b-2 border-white w-full "></div>
        </div>
      )}
    </div>
  );
};

const getFriend1Answer = () => {
  const answer = 165;
  const value = (answer / 400) * 100;
  return (
    <div className="absolute bottom-[1rem] flex flex-col justify-center items-center" style={{ left: `calc(${value}% - 20px)` }}>
      <div className="bg-blue-400 rounded-full z-10 top-[-3rem] h-[3rem] w-0.5 absolute" />
      <div className="w-10 h-10 z-20 rounded-full flex items-center justify-center pointer-events-none">
        <img src={faceImg} alt="Player" className="brightness-50 w-full h-full border-2 border-blue-400 rounded-full bg-yellow-400" />
        <p className="absolute w-full h-full flex flex-col items-center justify-center">
          <span className="h-5">{answer}</span>
        </p>
      </div>
    </div>
  );
};

const getFriend2Answer = () => {
  const answer = 88;
  const value = (answer / 400) * 100;
  return (
    <div className="absolute bottom-[3rem] flex flex-col justify-center items-center" style={{ left: `calc(${value}% - 20px)` }}>
      <div className="bg-purple-400 rounded-full z-10 top-[-1rem] h-[1rem] w-0.5 absolute" />
      <div className="w-10 h-10 z-20 rounded-full flex items-center justify-center pointer-events-none">
        <img src={faceImg} alt="Player" className="brightness-50 w-full h-full border-2 border-purple-400 rounded-full bg-yellow-400" />
        <p className="absolute w-full h-full flex flex-col items-center justify-center">
          <span className="h-5">{answer}</span>
        </p>{' '}
      </div>
    </div>
  );
};

const getStudioAnswer = () => {
  const answer = 110;
  const value = (answer / 400) * 100;
  return (
    <div className="absolute top-[3.5rem] flex flex-col justify-center items-center" style={{ left: `calc(${value}% - 20px)` }}>
      <div className="bg-purple-500 rounded-full z-10 bottom-[-1rem] h-[1.1rem] w-0.5 absolute" />
      <div className="w-8 h-8 z-20 rounded-full flex items-center justify-center pointer-events-none">
        <div className="w-full h-full rounded-full bg-purple-500" />
        <p className="absolute w-full h-full flex flex-col items-center justify-center">
          <span className="h-5">{answer}</span>
        </p>
      </div>
    </div>
  );
};

const getCorrectAnswer = () => {
  const answer = 220;
  const value = (answer / 400) * 100;
  return (
    <div className="absolute top-[0rem] flex flex-col justify-center items-center" style={{ left: `calc(${value}% - 20px)` }}>
      <div className="bg-green-400 rounded-full z-10 bottom-[-4.5rem] h-[4.6rem] w-0.5 absolute" />
      <div className="w-8 h-8 z-20 rounded-full flex items-center justify-center pointer-events-none">
        <div className="w-full h-full rounded-full bg-green-400" />
        <p className="absolute w-full h-full flex flex-col items-center justify-center">
          <span className="h-5">{answer}</span>
        </p>
      </div>
    </div>
  );
};

const getDenmarkAnswer = () => {
  const answer = 175;
  const value = (answer / 400) * 100;
  return (
    <div className="absolute top-[2rem] flex flex-col justify-center items-center" style={{ left: `calc(${value}% - 20px)` }}>
      <div className="bg-red-500 rounded-full z-10 bottom-[-2.5rem] h-[2.6rem] w-0.5 absolute" />
      <div className="w-8 h-8 z-20 rounded-full flex items-center justify-center pointer-events-none">
        <div className="w-full h-full rounded-full bg-red-500" />
        <p className="absolute w-full h-full flex flex-col items-center justify-center">
          <span className="h-5">{answer}</span>
        </p>
      </div>
    </div>
  );
};
